import { Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { IElement } from '@studio/domain/creativeset';
import { IVersionProperty } from '@studio/domain/creativeset/version';
import { IBfFeed } from '@studio/domain/feed';
import { DirtyProperties } from '@studio/domain/translation-page';
import { Observable } from 'rxjs';
import * as TranslationPageActions from './translation-page.actions';
import * as TranslationPageSelectors from './translation-page.selectors';

@Injectable({
    providedIn: 'root'
})
export class TranslationPageService {
    intialized$ = this.store.select(TranslationPageSelectors.getInitialized);
    loaded$ = this.store.select(TranslationPageSelectors.getLoaded);
    error$ = this.store.select(TranslationPageSelectors.getError);

    elements$ = this.store.select(TranslationPageSelectors.getElements);
    selectedElement$ = this.store.select(TranslationPageSelectors.getSelectedElement);
    selectedElementIndex$ = this.store.select(TranslationPageSelectors.getSelectedElementIndex);
    selectedElementPosition$ = this.store.select(TranslationPageSelectors.getSelectedElementPosition);
    isShowingAllElements$ = this.store.select(TranslationPageSelectors.getIsShowingAllElements);

    canSelectPrevious$ = this.store.select(TranslationPageSelectors.getCanSelectPrevious);
    canSelectNext$ = this.store.select(TranslationPageSelectors.getCanSelectNext);

    getTranslatableElementsAmount$ = this.store.select(
        TranslationPageSelectors.getTranslatableElementsAmount
    );
    hasTranslatableElements$ = this.store.select(TranslationPageSelectors.getHasTranslatableElements);

    dirtyProperties$ = this.store.select(TranslationPageSelectors.getDirtyProperties);

    hasDirtyProperties$ = this.store.select(TranslationPageSelectors.getHasDirtyProperties);

    resetInputs$ = this.actions$.pipe(ofType(TranslationPageActions.cancelTranslations));
    hasDirtyVersionProperties$ = this.store.select(TranslationPageSelectors.getHasDirtyProperties);

    feedSources$ = this.store.select(TranslationPageSelectors.getFeedSources);

    private dirtyPropertiesForVersionsSelectors: { [versionId: string]: Observable<DirtyProperties> } =
        {};

    constructor(
        private store: Store,
        private actions$: Actions
    ) {}

    selectDirtyVersionsFor(versionId: string): Observable<DirtyProperties> {
        this.dirtyPropertiesForVersionsSelectors[versionId] ??= this.store.select(
            TranslationPageSelectors.getDirtyPropertiesForVersion(versionId)
        );
        return this.dirtyPropertiesForVersionsSelectors[versionId];
    }

    init(): void {
        this.store.dispatch(TranslationPageActions.loadTranslationPage());
    }

    selectElement(element: IElement): void {
        this.store.dispatch(TranslationPageActions.selectElement({ elementId: element.id }));
    }

    selectNextElement(): void {
        this.store.dispatch(TranslationPageActions.selectNextElement());
    }
    selectPreviousElement(): void {
        this.store.dispatch(TranslationPageActions.selectPreviousElement());
    }

    upsertDirtyVersionProperty(versionId: string, versionProperty: IVersionProperty): void {
        this.store.dispatch(
            TranslationPageActions.upsertDirtyVersionProperty({
                versionId,
                versionProperty
            })
        );
    }
    removeDirtyVersionProperty(versionId: string, versionPropertyId: string): void {
        this.store.dispatch(
            TranslationPageActions.removeDirtyVersionProperty({
                versionId,
                versionPropertyId
            })
        );
    }

    cancelTranslations(): void {
        this.store.dispatch(TranslationPageActions.cancelTranslations());
    }

    saveDirtyVersions(): void {
        this.store.dispatch(TranslationPageActions.saveDirtyVersions());
    }
    showAllElements(): void {
        this.store.dispatch(TranslationPageActions.showAllElements());
    }

    hideAllElements(): void {
        this.store.dispatch(TranslationPageActions.hideAllElements());
    }

    setFeeds(feeds: IBfFeed[]): void {
        this.store.dispatch(TranslationPageActions.initBfFeeds({ feeds }));
    }
}
